import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { questionnaireService } from '../services/questionnaireService';
import { Questionnaire } from '../types';
import {
  TrashIcon,
  CopyIcon,
  CheckCircleIcon,
  XCircleIcon,
  TagIcon,
} from '../components/Icons';
import { useToast } from '../context/ToastContext';
import RichTextEditor from '../components/RichTextEditor';
import ConfirmModal from '../components/ConfirmModal';
import DropdownMenu, { DropdownMenuItem } from '../components/DropdownMenu';

const QuestionnairesPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteQuestionnaireId, setDeleteQuestionnaireId] = useState<number | null>(null);
  // 复制问卷 — 让用户起新标题
  const [duplicateSourceId, setDuplicateSourceId] = useState<number | null>(null);
  const [duplicateTitle, setDuplicateTitle] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    consentRequired: false,
    signatureRequired: false,
    consentContent: ''
  });

  useEffect(() => {
    fetchQuestionnaires();
  }, [page, search]);

  const fetchQuestionnaires = async () => {
    try {
      setLoading(true);
      const data = await questionnaireService.getQuestionnaires({
        search: search || undefined,
        page,
        size: 10,
      });

      setQuestionnaires(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error('Failed to fetch questionnaires:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      showToast('请输入问卷标题', 'warning');
      return;
    }

    try {
      await questionnaireService.createQuestionnaire(formData);
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        consentRequired: false,
        signatureRequired: false,
        consentContent: ''
      });
      fetchQuestionnaires();
      showToast('问卷创建成功', 'success');
    } catch (error) {
      console.error('Failed to create questionnaire:', error);
      showToast('创建失败，请重试', 'error');
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeleteQuestionnaireId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteQuestionnaireId === null) return;

    try {
      await questionnaireService.deleteQuestionnaire(deleteQuestionnaireId);
      setShowDeleteModal(false);
      setDeleteQuestionnaireId(null);
      fetchQuestionnaires();
      showToast('问卷删除成功', 'success');
    } catch (error) {
      console.error('Failed to delete questionnaire:', error);
      showToast('删除失败，请重试', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteQuestionnaireId(null);
  };

  const handleDuplicate = (id: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    // 打开标题输入弹窗，默认建议 "原标题 - 副本"
    const src = questionnaires.find((q) => q.id === id);
    setDuplicateSourceId(id);
    setDuplicateTitle(src ? `${src.title} - 副本` : '');
  };

  const handleDuplicateConfirm = async () => {
    if (duplicateSourceId == null) return;
    const trimmed = duplicateTitle.trim();
    if (!trimmed) {
      showToast('请填写新问卷标题', 'error');
      return;
    }
    try {
      setDuplicating(true);
      await questionnaireService.duplicateQuestionnaire(duplicateSourceId, trimmed);
      setDuplicateSourceId(null);
      setDuplicateTitle('');
      fetchQuestionnaires();
      showToast('问卷复制成功', 'success');
    } catch (error) {
      console.error('Failed to duplicate questionnaire:', error);
      showToast('复制失败，请重试', 'error');
    } finally {
      setDuplicating(false);
    }
  };

  const handleDuplicateCancel = () => {
    if (duplicating) return;
    setDuplicateSourceId(null);
    setDuplicateTitle('');
  };

  const handlePreview = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/questionnaires/${id}/preview`);
  };

  const handlePush = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/questionnaires/${id}/push`);
  };

  const handleStatistics = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/questionnaires/${id}/statistics`);
  };

  const handleDisable = async (id: number) => {
    try {
      await questionnaireService.disableQuestionnaire(id);
      fetchQuestionnaires();
      showToast('问卷已禁用', 'success');
    } catch (error) {
      console.error('Failed to disable questionnaire:', error);
      showToast('禁用失败，请重试', 'error');
    }
  };

  const handleEnable = async (id: number) => {
    try {
      await questionnaireService.enableQuestionnaire(id);
      fetchQuestionnaires();
      showToast('问卷已启用', 'success');
    } catch (error) {
      console.error('Failed to enable questionnaire:', error);
      showToast('启用失败，请重试', 'error');
    }
  };

  const handlePublish = async (id: number) => {
    try {
      await questionnaireService.publishQuestionnaire(id);
      fetchQuestionnaires();
      showToast('问卷发布成功', 'success');
    } catch (error) {
      console.error('Failed to publish questionnaire:', error);
      showToast('发布失败，请重试', 'error');
    }
  };

  const handleMarkVersion = async (id: number) => {
    try {
      await questionnaireService.markVersion(id);
      fetchQuestionnaires();
      showToast('版本标记成功', 'success');
    } catch (error) {
      console.error('Failed to mark version:', error);
      showToast('版本标记失败，请重试', 'error');
    }
  };

  const getStatusBadge = (status: string, displayName: string) => {
    const styles: Record<string, string> = {
      DRAFT: 'bg-amber-50 text-amber-700 border-amber-100',
      PUBLISHED: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      ARCHIVED: 'bg-slate-100 text-slate-600 border-slate-200',
    };
    const dotStyles: Record<string, string> = {
      DRAFT: 'bg-amber-500',
      PUBLISHED: 'bg-emerald-500',
      ARCHIVED: 'bg-slate-400',
    };
    const badgeClass = styles[status] || 'bg-slate-100 text-slate-600 border-slate-200';
    const dotClass = dotStyles[status] || 'bg-slate-400';
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border font-label whitespace-nowrap ${badgeClass}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`}></span>
        {displayName}
      </span>
    );
  };

  // Icons to cycle through for questionnaire rows
  const rowIcons = ['psychology', 'mood', 'history_edu', 'spa', 'self_improvement', 'neurology'];
  const rowIconBgs = [
    'bg-secondary-container',
    'bg-tertiary-container/30',
    'bg-secondary-container/50',
    'bg-primary-container',
    'bg-secondary-container',
    'bg-tertiary-container/30',
  ];
  const rowIconColors = [
    'text-secondary',
    'text-tertiary',
    'text-secondary',
    'text-primary',
    'text-secondary',
    'text-tertiary',
  ];

  // Generate pagination page numbers
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 5) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      pages.push(0);
      if (page > 2) pages.push('ellipsis');
      const start = Math.max(1, page - 1);
      const end = Math.min(totalPages - 2, page + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (page < totalPages - 3) pages.push('ellipsis');
      pages.push(totalPages - 1);
    }
    return pages;
  };

  return (
    <div className="p-8">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-on-surface font-headline">问卷管理</h1>
          <p className="text-on-surface-variant mt-1 text-sm font-body">创建和管理问卷调查，确保测评工具的有效性，跟踪参与者的互动情况。</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl font-medium shadow-teal hover:opacity-90 transition-all"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>add_circle</span>
          创建问卷
        </button>
      </div>

      {/* Filters & Insight Bento Grid */}
      <div className="grid grid-cols-12 gap-6 mb-6">
        {/* Search and Filter Bar */}
        <div className="col-span-12 lg:col-span-8 bg-surface-container-lowest p-6 rounded-[2rem] shadow-sm flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1 font-label">搜索问卷</label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant" style={{ fontSize: '20px' }}>search</span>
              <input
                type="text"
                placeholder="搜索问卷标题或描述..."
                className="w-full pl-10 pr-4 py-2.5 bg-surface-container-low border-none rounded-xl text-sm text-on-surface placeholder:text-outline-variant focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all font-label"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </div>
          </div>
          <div className="w-px h-10 bg-slate-100 hidden md:block"></div>
          <Link
            to="/questionnaires/trash"
            className="flex items-center gap-2 px-4 py-2.5 text-on-surface-variant hover:bg-slate-100 rounded-xl transition-colors font-label text-sm font-medium"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>delete</span>
            垃圾箱
          </Link>
        </div>

        {/* System Insight Card */}
        <div className="col-span-12 lg:col-span-4 bg-gradient-to-br from-tertiary to-tertiary-dim p-6 rounded-[2rem] text-on-tertiary shadow-xl relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4 opacity-80">
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>auto_awesome</span>
              <span className="text-[10px] font-bold uppercase tracking-widest font-label">系统洞察</span>
            </div>
            <h3 className="text-2xl font-bold mb-1">问卷概览</h3>
            <p className="text-sm opacity-90 leading-relaxed">共 {totalElements} 个问卷，管理和监控所有测评工具的状态与参与度。</p>
          </div>
          <div className="absolute -right-10 -bottom-10 opacity-20" style={{ transform: 'rotate(12deg)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '160px' }}>monitoring</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : questionnaires.length === 0 ? (
        <div className="text-center py-16 bg-surface-container-lowest rounded-[2.5rem] shadow-sm">
          <span className="material-symbols-outlined text-surface-dim mb-4" style={{ fontSize: '48px' }}>assignment</span>
          <h3 className="text-lg font-semibold text-on-surface mb-2 font-headline">暂无问卷</h3>
          <p className="text-on-surface-variant mb-6 font-body">开始创建您的第一个问卷</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary to-primary-dim text-on-primary rounded-xl font-bold shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            <span className="material-symbols-outlined filled" style={{ fontSize: '20px' }}>add_circle</span>
            创建问卷
          </button>
        </div>
      ) : (
        <>
          {/* Questionnaire Table */}
          <div className="bg-surface-container-lowest rounded-[2.5rem] shadow-sm p-2 overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: 1100 }}>
              <thead>
                <tr className="text-left">
                  <th className="px-6 py-5 text-xs font-semibold text-slate-500 font-label border-b border-slate-50 whitespace-nowrap">问卷名称</th>
                  <th className="px-4 py-5 text-xs font-semibold text-slate-500 font-label border-b border-slate-50 text-center whitespace-nowrap">状态</th>
                  <th className="px-4 py-5 text-xs font-semibold text-slate-500 font-label border-b border-slate-50 whitespace-nowrap">创建时间</th>
                  <th className="px-4 py-5 text-xs font-semibold text-slate-500 font-label border-b border-slate-50 text-center whitespace-nowrap">题数</th>
                  <th className="px-6 py-5 text-xs font-semibold text-slate-500 font-label border-b border-slate-50 text-right whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {questionnaires.map((questionnaire, index) => {
                  const isPublished = questionnaire.status === 'PUBLISHED';
                  const isDraft = questionnaire.status === 'DRAFT';
                  const isEnabled = questionnaire.enabled;
                  const iconIndex = index % rowIcons.length;

                  const dropdownMenuItems: DropdownMenuItem[] = [
                    ...(isDraft ? [{
                      label: '发布问卷',
                      icon: CheckCircleIcon,
                      onClick: () => handlePublish(questionnaire.id),
                    }] : []),
                    {
                      label: '复制问卷',
                      icon: CopyIcon,
                      onClick: () => handleDuplicate(questionnaire.id),
                    },
                    ...(isPublished ? [{
                      label: '标记版本',
                      icon: TagIcon,
                      onClick: () => handleMarkVersion(questionnaire.id),
                    }] : []),
                    ...(isDraft ? [
                      { divider: true as const },
                      {
                        label: '删除',
                        icon: TrashIcon,
                        onClick: () => handleDeleteClick(questionnaire.id),
                        danger: true,
                      },
                    ] : []),
                  ];

                  return (
                    <tr
                      key={questionnaire.id}
                      className="group hover:bg-slate-50/50 transition-colors"
                    >
                      {/* Questionnaire Name */}
                      <td className="px-6 py-5">
                        <div
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => navigate(`/questionnaires/${questionnaire.id}`)}
                        >
                          <div className={`w-11 h-11 rounded-2xl ${rowIconBgs[iconIndex]} flex items-center justify-center ${rowIconColors[iconIndex]} flex-shrink-0`}>
                            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{rowIcons[iconIndex]}</span>
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-on-surface text-base group-hover:text-primary transition-colors truncate">
                              {questionnaire.title}
                            </div>
                            <div className="text-xs text-slate-500 font-label mt-0.5 flex items-center gap-2 flex-wrap">
                              {questionnaire.questionnaireCode && (
                                <span className="px-1.5 py-0.5 bg-primary-50 text-primary-700 rounded text-[10px] font-mono font-medium whitespace-nowrap">
                                  {questionnaire.questionnaireCode}
                                </span>
                              )}
                              {questionnaire.description && (
                                <span className="line-clamp-1 max-w-md">{questionnaire.description}</span>
                              )}
                              {questionnaire.version && (
                                <span className="px-1.5 py-0.5 bg-surface-container-high text-on-surface-variant rounded text-[10px] font-medium whitespace-nowrap">
                                  {questionnaire.version}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-5 text-center whitespace-nowrap">
                        <div className="flex flex-col items-center gap-1">
                          {getStatusBadge(questionnaire.status, questionnaire.statusDisplayName)}
                          {isPublished && !isEnabled && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-100 font-label whitespace-nowrap">
                              <XCircleIcon size={10} />
                              已禁用
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Date Created */}
                      <td className="px-4 py-5 whitespace-nowrap">
                        <div className="text-sm font-medium text-on-surface">{new Date(questionnaire.createdAt).toLocaleDateString()}</div>
                        <div className="text-xs text-slate-400 font-label mt-0.5">{questionnaire.createdByUsername}</div>
                      </td>

                      {/* Question Count */}
                      <td className="px-4 py-5 text-center whitespace-nowrap">
                        <div className="text-lg font-semibold text-on-surface">{questionnaire.questionCount}</div>
                      </td>

                      {/* Actions —— 左：数据查看（统计分析）  右：问卷操作 */}
                      <td className="px-6 py-5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* —— 数据查看组 —— */}
                          {isPublished && (
                            <button
                              onClick={(e) => handleStatistics(questionnaire.id, e)}
                              className="h-9 px-3.5 inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 hover:bg-emerald-600 hover:text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap border border-emerald-200 hover:border-emerald-600"
                              title="查看统计 / 大报告 / 答题结果"
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>insights</span>
                              <span>统计分析</span>
                            </button>
                          )}

                          {/* 分隔符（仅当上面按钮存在时显示） */}
                          {isPublished && <div className="w-px h-6 bg-slate-200 mx-1" />}

                          {/* —— 问卷操作组 —— */}
                          <button
                            onClick={(e) => handlePreview(questionnaire.id, e)}
                            className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-primary hover:bg-primary-50 rounded-lg transition-colors"
                            title="预览问卷"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>visibility</span>
                          </button>

                          {isPublished && isEnabled && (
                            <button
                              onClick={(e) => handlePush(questionnaire.id, e)}
                              className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-primary hover:bg-primary-50 rounded-lg transition-colors"
                              title="推送给学生"
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>send</span>
                            </button>
                          )}

                          {isPublished && isEnabled && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDisable(questionnaire.id);
                              }}
                              className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="禁用问卷"
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>block</span>
                            </button>
                          )}

                          {isPublished && !isEnabled && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEnable(questionnaire.id);
                              }}
                              className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="启用问卷"
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>check_circle</span>
                            </button>
                          )}

                          <button
                            onClick={() => navigate(`/questionnaires/${questionnaire.id}`)}
                            className="h-9 px-3 inline-flex items-center gap-1 text-on-surface bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>edit_note</span>
                            <span>编辑</span>
                          </button>

                          <DropdownMenu
                            items={dropdownMenuItems}
                            align="right"
                            trigger={
                              <button
                                className="w-9 h-9 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                                title="更多操作"
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>more_vert</span>
                              </button>
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-6 border-t border-slate-50 flex justify-between items-center bg-surface-container-low/30">
                <div className="text-sm text-slate-500 font-label whitespace-nowrap">
                  共 {totalElements} 个问卷
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-primary transition-all disabled:opacity-30"
                  >
                    <span className="material-symbols-outlined">chevron_left</span>
                  </button>
                  {getPageNumbers().map((p, i) =>
                    p === 'ellipsis' ? (
                      <span key={`ellipsis-${i}`} className="px-2 text-slate-300">...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-10 h-10 flex items-center justify-center rounded-xl font-bold text-sm transition-all ${
                          page === p
                            ? 'bg-primary text-on-primary shadow-md shadow-primary/20'
                            : 'text-slate-500 hover:bg-white border border-transparent hover:border-slate-100'
                        }`}
                      >
                        {p + 1}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-primary transition-all disabled:opacity-30"
                  >
                    <span className="material-symbols-outlined">chevron_right</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-soft-lg">
            <h2 className="text-xl font-bold text-on-surface mb-6 font-headline">创建问卷</h2>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2 font-label">
                  问卷标题 *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-surface-container-high rounded-xl text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all font-body"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="请输入问卷标题"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2 font-label">
                  问卷描述
                </label>
                <textarea
                  className="w-full px-4 py-3 bg-surface-container-high rounded-xl text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all font-body"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="请输入问卷描述"
                />
              </div>

              {/* 知情同意书配置 */}
              <div className="pt-4" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
                <h3 className="text-lg font-semibold text-on-surface mb-4 font-headline">知情同意书配置</h3>

                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 rounded"
                      checked={formData.consentRequired}
                      onChange={(e) => setFormData({
                        ...formData,
                        consentRequired: e.target.checked,
                        signatureRequired: e.target.checked ? formData.signatureRequired : false
                      })}
                    />
                    <span className="text-sm font-medium text-on-surface font-body">启用知情同意书</span>
                  </label>
                  <p className="text-xs text-on-surface-variant ml-6 mt-1 font-label">
                    启用后，用户在答题前需要先阅读并同意知情同意书
                  </p>
                </div>

                {formData.consentRequired && (
                  <>
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 rounded"
                          checked={formData.signatureRequired}
                          onChange={(e) => setFormData({ ...formData, signatureRequired: e.target.checked })}
                        />
                        <span className="text-sm font-medium text-on-surface font-body">要求用户签字</span>
                      </label>
                      <p className="text-xs text-on-surface-variant ml-6 mt-1 font-label">
                        启用后，用户需要使用手写签字确认同意
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-on-surface mb-2 font-label">
                        知情同意书内容
                      </label>
                      <RichTextEditor
                        value={formData.consentContent}
                        onChange={(value) => setFormData({ ...formData, consentContent: value })}
                        placeholder="请输入知情同意书内容..."
                      />
                      <p className="text-xs text-on-surface-variant mt-1 font-label">
                        请详细说明问卷目的、数据使用方式、隐私保护等内容
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({
                    title: '',
                    description: '',
                    consentRequired: false,
                    signatureRequired: false,
                    consentContent: ''
                  });
                }}
                className="flex-1 px-4 py-3 text-on-surface-variant bg-surface-container-low rounded-xl hover:bg-surface-container-high transition-colors font-body"
              >
                取消
              </button>
              <button
                onClick={handleCreate}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-primary to-primary-dim text-on-primary rounded-xl shadow-lg shadow-primary/20 hover:opacity-90 transition-all font-body font-bold"
              >
                创建
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteModal}
        title="删除问卷"
        message="确定要删除这个问卷吗？删除后将移至垃圾箱，可在垃圾箱中恢复。"
        confirmText="删除"
        cancelText="取消"
        confirmButtonClass="bg-error-500 hover:bg-error-600"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* 复制问卷 — 让用户给新副本起名字 */}
      {duplicateSourceId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-on-surface mb-2">复制问卷</h2>
            <p className="text-sm text-on-surface-variant mb-4">
              请输入新问卷的标题。原问卷的题目、选项、跳转、指标、报告模板都会复制到新问卷中。
            </p>
            <label className="block text-sm font-medium text-on-surface mb-1.5">新问卷标题</label>
            <input
              autoFocus
              type="text"
              value={duplicateTitle}
              onChange={(e) => setDuplicateTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleDuplicateConfirm();
                if (e.key === 'Escape') handleDuplicateCancel();
              }}
              placeholder="例如：心理健康调查 2026 春季版"
              className="w-full h-11 px-3 bg-white border border-outline-variant rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary"
              maxLength={200}
            />
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={handleDuplicateCancel}
                disabled={duplicating}
                className="h-10 px-5 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low text-sm disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleDuplicateConfirm}
                disabled={duplicating || !duplicateTitle.trim()}
                className="h-10 px-5 gradient-primary text-on-primary rounded-xl shadow-teal text-sm font-medium disabled:opacity-50"
              >
                {duplicating ? '复制中…' : '复制'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnairesPage;
